import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, tokenStore } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface CardItem {
  id: string;
  nickname: string;
  bank: string;
  lastFour: string;
}
interface BankAccountItem {
  id: string;
  nickname: string;
  bank: string;
  accountNumberMasked?: string | null;
}
interface InstrumentResponse {
  cards: CardItem[];
  bankAccounts: BankAccountItem[];
}

type Preset = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'last_month' | 'this_year' | 'custom';
type ExportType = 'BOTH' | 'DEBIT' | 'CREDIT';

const PRESETS: Array<{ key: Preset; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This week' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'this_year', label: 'This year' },
  { key: 'custom', label: 'Custom' },
];

function todayIstYmd(): string {
  const ist = new Date(Date.now() + (5 * 60 + 30) * 60 * 1000);
  return ist.toISOString().slice(0, 10);
}

function presetRange(p: Preset): { start: string; end: string } | null {
  if (p === 'custom') return null;
  const now = new Date(Date.now() + (5 * 60 + 30) * 60 * 1000);
  const ymd = (d: Date) => d.toISOString().slice(0, 10);
  if (p === 'today') {
    const t = ymd(now);
    return { start: t, end: t };
  }
  if (p === 'yesterday') {
    const y = new Date(now.getTime() - 86_400_000);
    const t = ymd(y);
    return { start: t, end: t };
  }
  if (p === 'this_week') {
    const dow = (now.getUTCDay() + 6) % 7;
    const start = new Date(now.getTime() - dow * 86_400_000);
    return { start: ymd(start), end: ymd(now) };
  }
  if (p === 'this_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { start: ymd(start), end: ymd(now) };
  }
  if (p === 'last_month') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
    return { start: ymd(start), end: ymd(end) };
  }
  if (p === 'this_year') {
    const start = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    return { start: ymd(start), end: ymd(now) };
  }
  return null;
}

export function ExportReportPage() {
  const { user } = useAuth();
  const canExport = user?.role === 'ADMIN' || user?.role === 'ACCOUNTANT';

  const [preset, setPreset] = useState<Preset>('this_month');
  const [customStart, setCustomStart] = useState(todayIstYmd());
  const [customEnd, setCustomEnd] = useState(todayIstYmd());
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [selectedBankIds, setSelectedBankIds] = useState<Set<string>>(new Set());
  const [type, setType] = useState<ExportType>('BOTH');
  const [busy, setBusy] = useState<'csv' | 'pdf' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const instruments = useQuery<InstrumentResponse>({
    queryKey: ['export-instruments'],
    queryFn: () => api('/api/exports/instruments'),
    enabled: canExport,
  });

  const range = useMemo(() => {
    if (preset === 'custom') return { start: customStart, end: customEnd };
    return presetRange(preset)!;
  }, [preset, customStart, customEnd]);

  function toggleCard(id: string) {
    setSelectedCardIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function toggleBank(id: string) {
    setSelectedBankIds((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }
  function selectAllCards() {
    setSelectedCardIds(new Set(instruments.data?.cards.map((c) => c.id) ?? []));
  }
  function selectAllBanks() {
    setSelectedBankIds(new Set(instruments.data?.bankAccounts.map((b) => b.id) ?? []));
  }
  function clearAll() {
    setSelectedCardIds(new Set());
    setSelectedBankIds(new Set());
  }

  if (!canExport) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Export reports</CardTitle>
          <CardDescription>Only ADMIN or ACCOUNTANT roles can download transaction reports.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  async function download(format: 'csv' | 'pdf') {
    setErr(null);
    setOkMsg(null);
    setBusy(format);
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end, type, format });
      const hasCardPick = selectedCardIds.size > 0;
      const hasBankPick = selectedBankIds.size > 0;
      if (hasCardPick && !hasBankPick) {
        params.set('includeBanks', 'false');
        params.set('cardIds', [...selectedCardIds].join(','));
      } else if (hasBankPick && !hasCardPick) {
        params.set('includeCards', 'false');
        params.set('bankAccountIds', [...selectedBankIds].join(','));
      } else if (hasCardPick && hasBankPick) {
        params.set('cardIds', [...selectedCardIds].join(','));
        params.set('bankAccountIds', [...selectedBankIds].join(','));
      }
      const access = tokenStore.getAccess();
      const res = await fetch(`/api/exports/transactions?${params.toString()}`, {
        headers: access ? { Authorization: `Bearer ${access}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Export failed (${res.status}): ${txt.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m?.[1] ?? `txns_${range.start}_to_${range.end}.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setOkMsg(`Downloaded ${filename}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Transaction report</CardTitle>
          <CardDescription>
            Pick a date range, choose any combination of cards / bank accounts, then download as CSV or PDF.
            Wallet ledger movements are tracked separately and not included here.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Date range */}
          <div className="space-y-2">
            <Label>Date range</Label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition ' +
                    (preset === p.key
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'hover:bg-muted')
                  }
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'custom' ? (
              <div className="grid max-w-md grid-cols-2 gap-3 pt-2">
                <div>
                  <Label className="text-xs">From</Label>
                  <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                {range.start} → {range.end} (IST)
              </p>
            )}
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label>What to include</Label>
            <div className="flex gap-2">
              {(
                [
                  { v: 'BOTH', l: 'Debit + Credit' },
                  { v: 'DEBIT', l: 'Debit only' },
                  { v: 'CREDIT', l: 'Credit only' },
                ] as Array<{ v: ExportType; l: string }>
              ).map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setType(opt.v)}
                  className={
                    'rounded-full border px-3 py-1.5 text-sm transition ' +
                    (type === opt.v
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'hover:bg-muted')
                  }
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>

          {/* Instruments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Cards &amp; bank accounts</Label>
              <div className="flex gap-3 text-xs">
                <button type="button" onClick={selectAllCards} className="text-primary hover:underline">
                  All cards
                </button>
                <button type="button" onClick={selectAllBanks} className="text-primary hover:underline">
                  All banks
                </button>
                <button type="button" onClick={clearAll} className="text-muted-foreground hover:underline">
                  Clear
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Tick any combination — leave everything unchecked to include all instruments.
            </p>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-md border">
                <div className="border-b bg-muted/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide">
                  Credit cards ({instruments.data?.cards.length ?? 0})
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {(instruments.data?.cards ?? []).map((c) => (
                    <label
                      key={c.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCardIds.has(c.id)}
                        onChange={() => toggleCard(c.id)}
                      />
                      <span className="font-medium">{c.bank}</span>
                      <span className="text-muted-foreground">
                        {c.nickname} · ••{c.lastFour}
                      </span>
                    </label>
                  ))}
                  {!instruments.data?.cards.length && !instruments.isLoading ? (
                    <p className="px-2 py-3 text-sm text-muted-foreground">No active cards</p>
                  ) : null}
                </div>
              </div>
              <div className="rounded-md border">
                <div className="border-b bg-muted/30 px-3 py-2 text-xs font-semibold uppercase tracking-wide">
                  Bank accounts ({instruments.data?.bankAccounts.length ?? 0})
                </div>
                <div className="max-h-64 overflow-y-auto p-2">
                  {(instruments.data?.bankAccounts ?? []).map((b) => (
                    <label
                      key={b.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                    >
                      <input
                        type="checkbox"
                        checked={selectedBankIds.has(b.id)}
                        onChange={() => toggleBank(b.id)}
                      />
                      <span className="font-medium">{b.bank}</span>
                      <span className="text-muted-foreground">
                        {b.nickname}
                        {b.accountNumberMasked ? ` · ${b.accountNumberMasked}` : ''}
                      </span>
                    </label>
                  ))}
                  {!instruments.data?.bankAccounts.length && !instruments.isLoading ? (
                    <p className="px-2 py-3 text-sm text-muted-foreground">No active bank accounts</p>
                  ) : null}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {selectedCardIds.size} card{selectedCardIds.size === 1 ? '' : 's'} ·{' '}
              {selectedBankIds.size} bank account{selectedBankIds.size === 1 ? '' : 's'}{' '}
              {selectedCardIds.size === 0 && selectedBankIds.size === 0 ? '· (= all)' : 'selected'}
            </p>
          </div>

          {err ? <p className="text-sm text-destructive">{err}</p> : null}
          {okMsg ? <p className="text-sm text-emerald-700">{okMsg}</p> : null}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={() => download('csv')} disabled={busy !== null}>
              {busy === 'csv' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download CSV
            </Button>
            <Button variant="outline" onClick={() => download('pdf')} disabled={busy !== null}>
              {busy === 'pdf' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Download PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
